/**
 * Small badge that tells staff whether an inventory item is the shop's own
 * stock or held on consignment (and for whom). Renders "Shop owned" (gray) when
 * `consignorId` is null, or "Consigned · {name}" (orange) when set.
 *
 * Shell: (desktop) + (mobile). Staff-only — surfaces ownership on scan/search
 * results so a clerk knows at a glance whether a sale credits a consignor. Never
 * render in (kiosk): ownership/consignor data is staff-facing.
 *
 * @example <OwnershipBadge consignorId={r.consignor_id} consignorName={r.consignor_name} />
 */
import { Badge, type BadgeProps } from '@mantine/core';

export interface OwnershipBadgeProps {
  /** consignors.id when the item is on consignment; null/undefined = shop-owned. */
  consignorId: string | null | undefined;
  /** Resolved consignor display name; shown after "Consigned · " when present. */
  consignorName?: string | null;
  /** Badge size (defaults to "xs" to sit inline with other result chips). */
  size?: BadgeProps['size'];
}

export function OwnershipBadge({ consignorId, consignorName, size = 'xs' }: OwnershipBadgeProps) {
  if (!consignorId) {
    return (
      <Badge size={size} variant="light" color="gray">
        Shop owned
      </Badge>
    );
  }
  return (
    <Badge size={size} variant="light" color="orange">
      {consignorName ? `Consigned · ${consignorName}` : 'Consigned'}
    </Badge>
  );
}
